import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

/**
 * Toast message types
 */
export type ToastType = 'success' | 'error' | 'info';

/**
 * Toast message structure
 */
export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  timestamp: Date;
  duration: number;
}

/**
 * Toast Service
 *
 * Provides application-wide toast notifications using Bootstrap 5 toast styling.
 * Emits toast events via an observable stream that the ToastContainerComponent subscribes to.
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastSubject = new Subject<ToastMessage>();
  private counter = 0;

  /**
   * Observable stream of toast messages
   */
  toasts$: Observable<ToastMessage> = this.toastSubject.asObservable();

  /**
   * Default auto-dismiss duration in milliseconds
   */
  private readonly DEFAULT_DURATION = 5000;

  /**
   * Show a success toast
   *
   * @param message - Toast message text
   * @param duration - Auto-dismiss duration in ms (default: 5000)
   */
  success(message: string, duration: number = this.DEFAULT_DURATION): void {
    this.show('success', message, duration);
  }

  /**
   * Show an error toast
   *
   * @param message - Toast message text
   * @param duration - Auto-dismiss duration in ms (default: 5000)
   */
  error(message: string, duration: number = this.DEFAULT_DURATION): void {
    this.show('error', message, duration);
  }

  /**
   * Show an info toast
   *
   * @param message - Toast message text
   * @param duration - Auto-dismiss duration in ms (default: 5000)
   */
  info(message: string, duration: number = this.DEFAULT_DURATION): void {
    this.show('info', message, duration);
  }

  /**
   * Emit a toast message event
   */
  private show(type: ToastType, message: string, duration: number): void {
    this.counter++;
    const toast: ToastMessage = {
      id: `toast-${this.counter}-${Date.now()}`,
      type,
      message,
      timestamp: new Date(),
      duration,
    };
    this.toastSubject.next(toast);
  }
}
